import { Link } from "react-router-dom";
import { Check, Smartphone, RotateCcw } from "lucide-react";
import Reveal from "@/components/Reveal";
import { PREMIUM_PLANS, PREMIUM_FEATURES, PREMIUM_DISCLOSURE } from "@/config/site";

export const Premium = () => {
  return (
    <section id="premium" className="relative scroll-mt-24 overflow-hidden py-24 sm:py-32">
      <div className="aurora-blob left-1/2 top-0 h-[40vw] w-[40vw] -translate-x-1/2 bg-nova-cyan/10" />
      <div className="container-nova relative">
        <Reveal>
          <p className="label-eyebrow mb-4">Nova Premium</p>
          <h2 className="max-w-2xl font-heading text-3xl font-800 leading-tight tracking-tight text-white sm:text-5xl" style={{ fontWeight: 800 }}>
            Unlock the full <span className="text-gradient-accent">Nova experience</span>
          </h2>
          <p className="mt-4 max-w-2xl text-white/60">
            Choose the billing duration that suits you. All three plans unlock exactly the same Premium features. Only the length of the billing period is different.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-5 lg:grid-cols-3">
          {PREMIUM_PLANS.map((plan, i) => (
            <Reveal key={plan.name} delay={i * 0.08}>
              <div
                className={`relative flex h-full flex-col rounded-2xl border p-7 ${
                  plan.featured
                    ? "border-nova-emerald/40 bg-gradient-to-b from-nova-emerald/[0.07] to-transparent"
                    : "border-white/10 bg-white/[0.02]"
                }`}
                data-testid={`premium-plan-${plan.name.toLowerCase()}`}
              >
                {plan.featured && (
                  <span className="absolute -top-3 left-7 rounded-full bg-gradient-to-r from-nova-cyan to-nova-emerald px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-nova-bg">
                    {plan.tag}
                  </span>
                )}
                <div className="flex items-baseline justify-between">
                  <h3 className="font-heading text-2xl font-bold text-white">{plan.name}</h3>
                  {!plan.featured && <span className="text-xs font-semibold uppercase tracking-wider text-nova-cyan">{plan.tag}</span>}
                </div>
                <p className="mt-2 text-sm text-white/55">{plan.desc}</p>
                <div className="mt-5">
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-heading text-4xl font-bold text-white" data-testid={`premium-price-${plan.name.toLowerCase()}`}>{plan.price}</span>
                    <span className="text-sm text-white/50">{plan.period}</span>
                  </div>
                  {plan.note && (
                    <p className="mt-2.5 rounded-lg border border-nova-emerald/25 bg-nova-emerald/[0.06] px-3 py-2 text-xs leading-relaxed text-nova-emerald" data-testid="premium-annual-savings">
                      {plan.note}
                    </p>
                  )}
                </div>
                <span className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/12 bg-white/5 px-5 py-3 text-sm font-semibold text-white/80">
                  <Smartphone size={15} /> Available inside the app
                </span>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-8 grid gap-6 rounded-2xl border border-white/10 bg-white/[0.02] p-7 lg:grid-cols-[1fr_1px_1fr]">
            <div>
              <h3 className="font-heading text-lg font-bold text-white">What's included with Premium</h3>
              <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                {PREMIUM_FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-white/70">
                    <Check size={16} className="mt-0.5 shrink-0 text-nova-emerald" /> {f}
                  </li>
                ))}
              </ul>
            </div>
            <div className="hidden bg-white/10 lg:block" />
            <div>
              <h3 className="font-heading text-lg font-bold text-white">Billing & renewal</h3>
              <p className="mt-4 text-sm leading-relaxed text-white/60">{PREMIUM_DISCLOSURE}</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to="/privacy" className="text-sm text-nova-cyan hover:underline" data-testid="premium-privacy-link">Privacy Policy</Link>
                <span className="text-white/20">•</span>
                <Link to="/terms" className="text-sm text-nova-cyan hover:underline" data-testid="premium-terms-link">Terms of Use</Link>
                <span className="text-white/20">•</span>
                <Link to="/support" className="text-sm text-nova-cyan hover:underline" data-testid="premium-support-link">Support</Link>
                <span className="text-white/20">•</span>
                <Link to="/support#restore" className="inline-flex items-center gap-1 text-sm text-nova-cyan hover:underline" data-testid="premium-restore-link">
                  <RotateCcw size={13} /> Restore Purchases
                </Link>
              </div>
            </div>
          </div>
        </Reveal>

        <p className="mt-6 text-xs text-white/40">
          Nova Premium is a digital subscription purchased through Apple In-App Purchase on iOS. There is no external web checkout for Premium. Prices are shown in GBP for the UK App Store; Apple may display a different local price based on your App Store region.
        </p>
      </div>
    </section>
  );
};

export default Premium;
